import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, of, tap } from 'rxjs';
import { ClassCourseWithEnrolmentsDTO } from 'src/app/models/class-coursewithenrolments-dto.model';
import { LearnerSearchForm } from 'src/app/models/learner-search-form.model';
import { LearnerDTO } from 'src/app/models/learner.model';
import { WorkerExemptionRequestDto } from 'src/app/models/worker-exemption-request.model';
import { WorkerTrainingScheduleDTO } from 'src/app/models/worker-training-schedules.model';
import { LearnerCertificateStatus, TPWorker } from 'src/app/models/worker.model';
import { environment } from 'src/environments/environment';
import { InquiryRequestDTO } from '../../models/inquiry-request.model';
import { WorkerServiceInterface } from './worker.service.interface';
import { ClassEnrolment } from 'src/app/models/class-enrolment.model';
import { WorkerSearchCriteria } from 'src/app/models/worker-search-criteria.model';
import { SurveyForm } from 'src/app/models/survey-form.model';
import { PSUser } from 'src/app/models/ps-user.model';

@Injectable({ providedIn: 'root' })
export class WorkerService implements WorkerServiceInterface {

  private worker?: TPWorker;
  private filterSortPage = false;
  filterSortPageSubject = new Subject<boolean>();

  setFilterSortFlag(flagValue: boolean) {
    this.filterSortPage = flagValue;
    this.filterSortPageSubject.next(flagValue);
  }

  getFilterSortFlag() {
    return this.filterSortPage;
  }

  constructor(private httpClient: HttpClient) { }

  SaveWorker(worker: TPWorker): Observable<string> {
    return this.httpClient.post<string>(environment.apiBaseUrl + `Worker/SaveWorkerProfile`, worker);
  }

  SetUpWorkerProfile(worker: TPWorker): Observable<string> {
    this.ClearCurrentWorker()
    return this.httpClient.post<string>(environment.apiBaseUrl + `Worker/SetUpWorkerProfile`, worker);
  }

  SearchWorkerByCourseId(courseId: string): Observable<TPWorker[]> {
    return this.httpClient.get<any[]>(environment.apiBaseUrl + `Worker/SearchWorkerByCourseId/${courseId}`);
  }

  GetWorkers(): Observable<LearnerDTO[]> {
    return this.httpClient.get<any[]>(environment.apiBaseUrl + `Worker/GetWorkersForTrainingProvider`);
  }

  SearchWorkersForTp(learnerSearchForm: LearnerSearchForm): Observable<TPWorker[]> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `Worker/SearchWorkersForTp`, learnerSearchForm);
  }

  SaveWorkerExemptionRequest(workerExemptionRequestModel: WorkerExemptionRequestDto): Observable<WorkerExemptionRequestDto> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `Worker/SaveWorkerExemptionRequest`, workerExemptionRequestModel);
  }

  GetWorkerExemptionRequestByWorkerIdCourseId(workerId: string, courseId: string): Observable<WorkerExemptionRequestDto> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetWorkerExemptionRequest/${workerId}/${courseId}`);
  }

  GetWorkerExemptionRequest(workerExemptionRequestId: string): Observable<WorkerExemptionRequestDto> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetWorkerExemptionRequest/${workerExemptionRequestId}`);
  }

  GetUpcomingTrainingsforWorker(workerId: string): Observable<WorkerTrainingScheduleDTO[]> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetUpcomingTrainingsforWorker/${workerId}`);
  }

  GetCurrentWorker(): Observable<TPWorker> {
    if (this.worker) return of(this.worker!);
    else return this.httpClient.get<TPWorker>(environment.apiBaseUrl + `PublicSecure/GetCurrentWorker/`).pipe(tap(x => this.worker = x));
  }
  ClearCurrentWorker() { this.worker = undefined; }

  SubmitInquiryRequest(request: InquiryRequestDTO): Observable<boolean> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `Worker/SubmitInquiryRequest`, request);
  }

  GetEnrolmentDetailsByGroupIdOrRegistrationId(registrationId: string): Observable<ClassCourseWithEnrolmentsDTO> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetEnrolmentDetailsByGroupIdOrRegistrationId/${registrationId}`);
  }

  GetWorkerById(workerId: string): Observable<TPWorker> {
    //TO-DO: Cleanup/Remove after final pen/go live testing
    //return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetWorkerForWorker/${workerId}`);
    return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetWorkerDetailsForLoggedInWorker`);
  }

  SaveCourseEnrolments(enrollments: ClassEnrolment[]): Observable<string> {
    return this.httpClient.post<string>(environment.apiBaseUrl + `course/SaveCourseEnrolmentsForPublic`, enrollments);
  }

  DownloadCertificate(en: boolean, workerId: string, certificateCode: string, certVersion: string): Observable<any> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `Worker/DownloadCertificate`, { en: en, workerId: workerId, certificateCode: certificateCode, certVersion: certVersion });
  }

  EmailCertificate(en: boolean, workerId: string, certificateCode: string, certVersion: string, learnerEmail: string, additionalEmails: string[]): Observable<any> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `Worker/EmailCertificate`, { en: en, workerId: workerId, certificateCode: certificateCode, certVersion: certVersion, email: learnerEmail, additionalEmails: additionalEmails });
  }

  PreviewCertificate(en: boolean, workerId: string, certificateCode: string, certVersion: string): Observable<any> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `Worker/PreviewCertificate`, { en: en, workerId: workerId, certificateCode: certificateCode, certVersion: certVersion });
  }

  GetLearnerCertificateDetails(workerId: string, programCode: string): Observable<LearnerCertificateStatus> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `Worker/GetLearnerCertificateDetails/${workerId}/${programCode}`);
  }

  SearchWorkerForEmployer(workerSearchCriteria: WorkerSearchCriteria): Observable<TPWorker[]> {
    return this.httpClient.post<any[]>(environment.apiBaseUrl + `Worker/SearchWorkerForEmployer`,workerSearchCriteria);
  }

  DownloadCertificateWithERCredential(en: boolean, workerId: string, certificateCode: string, certVersion: string): Observable<any> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `Worker/DownloadCertificateWithERCredential`, { en: en, workerId: workerId, certificateCode: certificateCode, certVersion: certVersion });
  }

  SaveFeedbackSurvey(request: SurveyForm): Observable<boolean> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `Worker/SaveFeedbackSurvey`, request);
  }

  GetPersonDetailsByEmail(email: string): Observable<PSUser | null> {
    return this.httpClient.get<PSUser>(environment.apiBaseUrl + `Worker/GetPersonByEmail/${email}`);
  }

  GetCurrentWorkerByPersonId(personId: string): Observable<TPWorker> {
    return this.httpClient.get<TPWorker>(environment.apiBaseUrl + `Worker/GetCurrentWorkerByPersonId/${personId}`);
  }
}
